import { Injectable, OnDestroy } from '@angular/core'
import {
  NavigationEnd,
  Params,
  Router,
  convertToParamMap,
  ParamMap,
} from '@angular/router'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { filter, takeUntil } from 'rxjs/operators'

@Injectable({
  providedIn: 'root',
})
export class QueryParamsService implements OnDestroy {
  private readonly queryParamsSubject = new BehaviorSubject<Params>({})
  private readonly queryParamMapSubject = new BehaviorSubject<ParamMap>(
    convertToParamMap({}),
  )
  private readonly destroy$ = new Subject<void>()
  private lastParams: Params = {}

  constructor(private readonly router: Router) {
    this.emitCurrentParams()

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.emitCurrentParams())
  }

  get queryParams$(): Observable<Params> {
    return this.queryParamsSubject.asObservable()
  }

  get queryParamMap$(): Observable<ParamMap> {
    return this.queryParamMapSubject.asObservable()
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
    this.queryParamsSubject.complete()
    this.queryParamMapSubject.complete()
  }

  private emitCurrentParams(): void {
    const params = this.router.routerState.snapshot.root.queryParams || {}

    if (this.areParamsEqual(this.lastParams, params)) {
      return
    }

    this.lastParams = { ...params }
    this.queryParamsSubject.next(this.lastParams)
    this.queryParamMapSubject.next(convertToParamMap(this.lastParams))
  }

  private areParamsEqual(previous: Params, current: Params): boolean {
    const previousKeys = Object.keys(previous)
    const currentKeys = Object.keys(current)

    if (previousKeys.length !== currentKeys.length) {
      return false
    }

    for (const key of previousKeys) {
      if (previous[key] !== current[key]) {
        return false
      }
    }

    return true
  }
}
